import { useState, useMemo } from 'react'
import { CaretDownIcon, CaretUpIcon, FunnelIcon } from '@phosphor-icons/react'
import {
    ScoopButton,
    ScoopText,
    ScoopSelector,
    ScoopChip,
    ScoopSpinner,
    ScoopRow,
    ScoopColumn,
    ScoopCenter,
} from '../../../../../ui'
import styles from './HistoryViewer.module.scss'

const TYPE_OPTIONS = [
    { label: 'All Types', value: 'all' },
    { label: 'Feedback', value: 'feedback' },
    { label: 'Manual', value: 'manual' },
    { label: 'System', value: 'system' },
]

const TYPE_COLORS = {
    feedback: 'primary',
    manual: 'warning',
    system: 'default',
}

/**
 * HistoryViewer - Read-only viewer for learning_history.json
 *
 * @param {Object} data - Current history content
 * @param {boolean} isLoading - Whether data is loading
 */
export const HistoryViewer = ({ data, isLoading }) => {
    const [typeFilter, setTypeFilter] = useState('all')
    const [sortOrder, setSortOrder] = useState('desc')
    const [expandedRows, setExpandedRows] = useState(new Set())

    // Parse and filter history entries
    const entries = useMemo(() => {
        // Handle various data structures from API
        let historyEntries = []

        if (Array.isArray(data)) {
            historyEntries = data
        } else if (Array.isArray(data?.entries)) {
            historyEntries = data.entries
        } else if (Array.isArray(data?.history)) {
            historyEntries = data.history
        } else if (data && typeof data === 'object') {
            // If data is an object, try to extract entries from values
            // Filter out metadata keys starting with _
            historyEntries = Object.entries(data)
                .filter(([key]) => !key.startsWith('_'))
                .map(([key, value]) => {
                    if (typeof value === 'object' && value !== null) {
                        return { id: key, ...value }
                    }
                    return null
                })
                .filter(Boolean)
        }

        return historyEntries
            .filter((entry) => typeFilter === 'all' || entry.type === typeFilter)
            .sort((a, b) => {
                const dateA = new Date(a.timestamp || a.date || 0)
                const dateB = new Date(b.timestamp || b.date || 0)
                return sortOrder === 'desc' ? dateB - dateA : dateA - dateB
            })
    }, [data, typeFilter, sortOrder])

    const toggleRow = (index) => {
        setExpandedRows((prev) => {
            const newSet = new Set(prev)
            if (newSet.has(index)) {
                newSet.delete(index)
            } else {
                newSet.add(index)
            }
            return newSet
        })
    }

    const toggleSort = () => {
        setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'))
    }

    const formatDate = (timestamp) => {
        if (!timestamp) return 'Unknown'
        const date = new Date(timestamp)
        return date.toLocaleString()
    }

    if (isLoading) {
        return (
            <ScoopCenter className={styles.loading}>
                <ScoopSpinner size={32} />
                <ScoopText>Loading history...</ScoopText>
            </ScoopCenter>
        )
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleSection}>
                    <ScoopText as="h2" className={styles.title}>
                        Change Log
                    </ScoopText>
                    <ScoopText className={styles.description}>
                        View the history of changes made to this workspace's configuration.
                    </ScoopText>
                </div>
            </div>

            <div className={styles.content}>
                {/* Filters */}
                <ScoopRow className={styles.filters}>
                    <ScoopRow className={styles.filterGroup}>
                        <FunnelIcon size={16} className={styles.filterIcon} />
                        <ScoopSelector
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            options={TYPE_OPTIONS}
                            size="sm"
                            removeMargin
                        />
                    </ScoopRow>
                    <ScoopButton
                        variant="grey"
                        size="sm"
                        onClick={toggleSort}
                        leftSection={
                            sortOrder === 'desc' ? (
                                <CaretDownIcon size={16} />
                            ) : (
                                <CaretUpIcon size={16} />
                            )
                        }
                    >
                        {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
                    </ScoopButton>
                </ScoopRow>

                {/* History Table */}
                <div className={styles.table}>
                    <div className={styles.tableHeader}>
                        <ScoopText className={styles.colTimestamp}>Timestamp</ScoopText>
                        <ScoopText className={styles.colType}>Type</ScoopText>
                        <ScoopText className={styles.colSection}>Section</ScoopText>
                        <ScoopText className={styles.colChange}>Change</ScoopText>
                        <ScoopText className={styles.colSource}>Source</ScoopText>
                    </div>

                    {entries.length === 0 ? (
                        <div className={styles.emptyRow}>
                            <ScoopText className={styles.emptyText}>
                                No history entries found
                                {typeFilter !== 'all' ? ' for this filter' : ''}.
                            </ScoopText>
                        </div>
                    ) : (
                        entries.map((entry, index) => (
                            <div key={index} className={styles.tableRow}>
                                <div className={styles.rowContent} onClick={() => toggleRow(index)}>
                                    <ScoopText className={styles.colTimestamp}>
                                        {formatDate(entry.timestamp || entry.date)}
                                    </ScoopText>
                                    <div className={styles.colType}>
                                        <ScoopChip
                                            label={entry.type || 'unknown'}
                                            size="small"
                                            color={TYPE_COLORS[entry.type] || 'default'}
                                        />
                                    </div>
                                    <ScoopText className={styles.colSection}>
                                        {entry.section || entry.component || 'General'}
                                    </ScoopText>
                                    <ScoopText className={styles.colChange}>
                                        {entry.summary || entry.change || entry.description}
                                    </ScoopText>
                                    <ScoopText className={styles.colSource}>
                                        {entry.source || entry.user || 'System'}
                                    </ScoopText>
                                </div>

                                {/* Expanded Details */}
                                {expandedRows.has(index) && (
                                    <div className={styles.rowDetails}>
                                        {entry.reason && (
                                            <div className={styles.detailItem}>
                                                <ScoopText className={styles.detailLabel}>
                                                    Reason:
                                                </ScoopText>
                                                <ScoopText className={styles.detailValue}>
                                                    {entry.reason}
                                                </ScoopText>
                                            </div>
                                        )}
                                        {entry.before && (
                                            <div className={styles.detailItem}>
                                                <ScoopText className={styles.detailLabel}>
                                                    Before:
                                                </ScoopText>
                                                <code className={styles.detailCode}>
                                                    {typeof entry.before === 'object'
                                                        ? JSON.stringify(entry.before, null, 2)
                                                        : entry.before}
                                                </code>
                                            </div>
                                        )}
                                        {entry.after && (
                                            <div className={styles.detailItem}>
                                                <ScoopText className={styles.detailLabel}>
                                                    After:
                                                </ScoopText>
                                                <code className={styles.detailCode}>
                                                    {typeof entry.after === 'object'
                                                        ? JSON.stringify(entry.after, null, 2)
                                                        : entry.after}
                                                </code>
                                            </div>
                                        )}
                                        {entry.details && (
                                            <div className={styles.detailItem}>
                                                <ScoopText className={styles.detailLabel}>
                                                    Details:
                                                </ScoopText>
                                                <ScoopText className={styles.detailValue}>
                                                    {entry.details}
                                                </ScoopText>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Stats */}
                {entries.length > 0 && (
                    <ScoopRow className={styles.stats}>
                        <ScoopText className={styles.statsText}>
                            Showing {entries.length} {entries.length === 1 ? 'entry' : 'entries'}
                            {typeFilter !== 'all' && ` of type "${typeFilter}"`}
                        </ScoopText>
                    </ScoopRow>
                )}
            </div>
        </div>
    )
}
